<template lang="pug">
  div
    .mb-6
      h1.display-1.mb-2 Filter Menu Component
      p.text--secondary A reusable filter menu component that provides consistent UI patterns for filter implementations across the application.

    // Filter Menu Examples
    .component-section.mb-8#basic-filter-menu
      h3.mb-4.cursor-pointer(@click="navigateToSection('basic-filter-menu')") Basic Filter Menu Examples
      .variations-grid
        .variation-item.mb-6(v-for="variation in filterMenuVariations" :key="variation.name" :id="variation.id")
          .variation-header.mb-3
            h4.cursor-pointer(@click="navigateToSection(variation.id)") {{ variation.name }}
            p.text--secondary.caption {{ variation.description }}

          .variation-preview.mb-4.pa-4.border.rounded
            .mb-2
              span.font-weight-medium {{ variation.label }}
              small.text--secondary.ml-2(v-if="variation.hint") {{ variation.hint }}

            // Filter Menu Implementation - Text Input
            yz-filter-menu(
              v-if="variation.type === 'text-input'"
              :label="variation.filterLabel"
              :is-active="variation.data.isActive"
              :display-value="variation.data.displayValue"
              :disable-apply="variation.disableCondition()"
              :loading="variation.data.loading"
              v-bind="variation.props"
              @clear="variation.onClear"
              @apply="variation.onApply"
            )
              .pa-4
                y-select.mb-2(
                  :items="variation.options"
                  item-text="name"
                  item-value="id"
                  v-model="variation.data.option"
                  small
                  attach
                )
                .d-flex.align-start
                  v-icon.mx-2(size="16" color="primary") $yzi-corner-down-right
                  v-text-field.round-xl(
                    v-model="variation.data.value"
                    hide-details
                    outlined
                    dense
                    placeholder="Type a value"
                  )

            // Filter Menu Implementation - Checkbox List
            yz-filter-menu(
              v-if="variation.type === 'checkbox-list'"
              :label="variation.filterLabel"
              :is-active="variation.data.isActive"
              :display-value="variation.data.displayValue"
              :disable-apply="variation.disableCondition()"
              :loading="variation.data.loading"
              v-bind="variation.props"
              @clear="variation.onClear"
              @apply="variation.onApply"
            )
              template(v-slot:header)
                .font-semibold.text-sm.pa-4 Filter by {{ variation.filterLabel.toLowerCase() }}

              div
                div(v-for="option in variation.options" :key="option.value")
                  .d-flex.text-sm.px-4.py-3.cursor-pointer.hover-bg(@click="variation.toggleItem(option.value)")
                    yc-checkbox(:input-value="variation.data.selected.includes(option.value)")
                    .ml-4 {{ option.label }}
                  v-divider.basic300

            // Filter Menu Implementation - Searchable List
            yz-filter-menu(
              v-if="variation.type === 'searchable-list'"
              :label="variation.filterLabel"
              :is-active="variation.data.isActive"
              :display-value="variation.data.displayValue"
              :disable-apply="variation.disableCondition()"
              :loading="variation.data.loading"
              v-bind="variation.props"
              @clear="variation.onClear"
              @apply="variation.onApply"
            )
              .white
                .menu-header.white.sticky.top-0.z-1
                  .font-semibold.text-sm.pa-4 Filter by {{ variation.filterLabel.toLowerCase() }}
                  v-text-field.text-sm.round-xl.mx-4.mb-3(
                    v-model="variation.data.search"
                    outlined
                    placeholder="Search"
                    dense
                    hide-details
                  )
                  v-divider

                v-skeleton-loader.pa-4(v-if="variation.data.loading" type="text@3")
                div(v-else)
                  template(v-if="variation.filteredItems().length > 0")
                    div(v-for="item in variation.filteredItems()" :key="item.value")
                      .d-flex.text-sm.px-4.py-3.cursor-pointer.hover-bg(@click="variation.toggleItem(item.value)")
                        yc-checkbox(:input-value="variation.data.selected.includes(item.value)")
                        .ml-4 {{ item.label }}
                      v-divider.basic300
                  template(v-else)
                    .text-center.py-8.text-xs.neutral500--text No items found

          .variation-code
            .code-header.d-flex.align-center.mb-2
              yz-btn(icon-only size="md" :icon="copiedCode === variation.code ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === variation.code ? 'success' : ''" @click="copyCode(variation.code)")
              span.font-weight-medium.ml-2 Code

            pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
              code {{ variation.code }}

    // API Documentation
    .component-section.mb-8#api
      h3.mb-4.cursor-pointer(@click="navigateToSection('api')") API

      h4.mb-3 Props
      .props-table.mb-6
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Prop
                th Type
                th Default
                th Description
            tbody
              tr(v-for="prop in filterMenuProps" :key="prop.name")
                td.font-weight-medium.text-no-wrap {{ prop.name }}
                td.text--secondary.text-no-wrap {{ prop.type }}
                td.text--secondary.text-no-wrap {{ prop.default }}
                td.single-line {{ prop.description }}

      h4.mb-3 Slots
      .slots-table.mb-6
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Slot
                th Description
            tbody
              tr(v-for="slot in filterMenuSlots" :key="slot.name")
                td.font-weight-medium.text-no-wrap {{ slot.name }}
                td.single-line {{ slot.description }}

      h4.mb-3 Events
      .events-table
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Event
                th Parameters
                th Description
            tbody
              tr(v-for="event in filterMenuEvents" :key="event.name")
                td.font-weight-medium.text-no-wrap {{ event.name }}
                td.text--secondary.text-no-wrap {{ event.parameters }}
                td.single-line {{ event.description }}
</template>

<script>
export default {
  data() {
    return {
      copiedCode: null,
      // Text Input Filter Data
      nameData: {
        option: 'name__contains',
        value: '',
        isActive: false,
        displayValue: '',
        loading: false
      },
      // Checkbox List Filter Data
      statusData: {
        selected: [],
        isActive: false,
        displayValue: '',
        loading: false
      },
      // Searchable List Filter Data
      categoryData: {
        selected: [],
        search: '',
        isActive: false,
        displayValue: '',
        loading: false
      }
    }
  },
  computed: {
    filterMenuVariations() {
      return [
        {
          name: 'Text Input Filter',
          id: 'text-input-filter',
          description: 'Basic filter with dropdown selection and text input field',
          label: 'Text Input Filter',
          hint: 'Select option and enter value',
          filterLabel: 'Name',
          type: 'text-input',
          data: this.nameData,
          options: [
            { id: 'name', name: 'Is' },
            { id: 'name__ne', name: 'Is not' },
            { id: 'name__contains', name: 'Contains' },
            { id: 'name__exclude', name: 'Does not contain' }
          ],
          disableCondition: () => !this.nameData.value,
          onClear: () => {
            this.nameData.value = ''
            this.nameData.isActive = false
            this.nameData.displayValue = ''
          },
          onApply: () => {
            this.nameData.isActive = true
            const optionName = this.filterMenuVariations[0].options.find(o => o.id === this.nameData.option)?.name || ''
            this.nameData.displayValue = `${optionName} ${this.nameData.value}`
          },
          props: {},
          code: `yz-filter-menu(
  label="Name"
  :is-active="isActive"
  :display-value="displayValue"
  :disable-apply="!value"
  @clear="clearFilter"
  @apply="applyFilter"
)
  .pa-4
    y-select.mb-2(
      :items="options"
      v-model="option"
      small
      attach
    )
    .d-flex.align-start
      v-icon.mx-2(size="16" color="primary") $yzi-corner-down-right
      v-text-field.round-xl(
        v-model="value"
        hide-details
        outlined
        dense
        placeholder="Type a value"
      )`
        },
        {
          name: 'Checkbox List Filter',
          id: 'checkbox-list-filter',
          description: 'Filter with checkbox list for multiple selections',
          label: 'Checkbox List Filter',
          hint: 'Select one or more options',
          filterLabel: 'Status',
          type: 'checkbox-list',
          data: this.statusData,
          options: [
            { value: 'pending', label: 'Pending' },
            { value: 'active', label: 'Active' },
            { value: 'completed', label: 'Completed' },
            { value: 'cancelled', label: 'Cancelled' }
          ],
          disableCondition: () => this.statusData.selected.length === 0,
          toggleItem: (value) => {
            const index = this.statusData.selected.indexOf(value)
            if (index > -1) {
              this.statusData.selected.splice(index, 1)
            } else {
              this.statusData.selected.push(value)
            }
          },
          onClear: () => {
            this.statusData.selected = []
            this.statusData.isActive = false
            this.statusData.displayValue = ''
          },
          onApply: () => {
            this.statusData.isActive = true
            const count = this.statusData.selected.length
            if (count <= 2) {
              const labels = this.statusData.selected.map(val =>
                this.filterMenuVariations[1].options.find(o => o.value === val)?.label
              )
              this.statusData.displayValue = labels.join(', ')
            } else {
              this.statusData.displayValue = `${count} selected`
            }
          },
          props: {},
          code: `yz-filter-menu(
  label="Status"
  :is-active="isActive"
  :display-value="displayValue"
  :disable-apply="selected.length === 0"
  @clear="clearFilter"
  @apply="applyFilter"
)
  template(v-slot:header)
    .font-semibold.text-sm.pa-4 Filter by status
  div(v-for="option in options" :key="option.value")
    .d-flex.text-sm.px-4.py-3.cursor-pointer(@click="toggleItem(option)")
      yc-checkbox(:input-value="isSelected(option)")
      .ml-4 {{ option.label }}
    v-divider`
        },
        {
          name: 'Searchable List Filter',
          id: 'searchable-list-filter',
          description: 'Filter with search field and scrollable checkbox list',
          label: 'Searchable List Filter',
          hint: 'Search and select items',
          filterLabel: 'Category',
          type: 'searchable-list',
          data: this.categoryData,
          options: [
            { value: 'electronics', label: 'Electronics' },
            { value: 'clothing', label: 'Clothing' },
            { value: 'food', label: 'Food & Beverage' },
            { value: 'health', label: 'Health & Beauty' },
            { value: 'home', label: 'Home & Garden' },
            { value: 'sports', label: 'Sports & Outdoors' }
          ],
          disableCondition: () => this.categoryData.selected.length === 0,
          filteredItems: () => {
            if (!this.categoryData.search) {
              return this.filterMenuVariations[2].options
            }
            const query = this.categoryData.search.toLowerCase()
            return this.filterMenuVariations[2].options.filter(item =>
              item.label.toLowerCase().includes(query)
            )
          },
          toggleItem: (value) => {
            const index = this.categoryData.selected.indexOf(value)
            if (index > -1) {
              this.categoryData.selected.splice(index, 1)
            } else {
              this.categoryData.selected.push(value)
            }
          },
          onClear: () => {
            this.categoryData.selected = []
            this.categoryData.search = ''
            this.categoryData.isActive = false
            this.categoryData.displayValue = ''
          },
          onApply: () => {
            this.categoryData.isActive = true
            const count = this.categoryData.selected.length
            if (count <= 2) {
              const labels = this.categoryData.selected.map(val =>
                this.filterMenuVariations[2].options.find(o => o.value === val)?.label
              )
              this.categoryData.displayValue = labels.join(', ')
            } else {
              this.categoryData.displayValue = `${count} selected`
            }
          },
          props: {
            maxHeight: 420,
            showHeader: false
          },
          code: `yz-filter-menu(
  label="Category"
  :is-active="isActive"
  :display-value="displayValue"
  :disable-apply="selected.length === 0"
  :max-height="420"
  :show-header="false"
  @clear="clearFilter"
  @apply="applyFilter"
)
  .white
    .menu-header.white
      .font-semibold.text-sm.pa-4 Filter by category
      v-text-field.text-sm.round-xl.mx-4.mb-3(
        v-model="search"
        outlined
        placeholder="Search"
        dense
        hide-details
      )
      v-divider

    v-skeleton-loader.pa-4(v-if="loading" type="text@3")
    div(v-else)
      div(v-for="item in filteredItems" :key="item.value")
        .d-flex.text-sm.px-4.py-3.cursor-pointer(@click="toggleItem(item)")
          yc-checkbox(:input-value="isSelected(item)")
          .ml-4 {{ item.label }}
        v-divider`
        }
      ]
    },
    filterMenuProps() {
      return [
        {
          name: 'label',
          type: 'String',
          default: 'required',
          description: 'Filter label text displayed on the button'
        },
        {
          name: 'displayValue',
          type: 'String',
          default: "''",
          description: 'Value text shown when filter is active'
        },
        {
          name: 'isActive',
          type: 'Boolean',
          default: 'false',
          description: 'Whether the filter is currently applied'
        },
        {
          name: 'loading',
          type: 'Boolean',
          default: 'false',
          description: 'Shows loading spinner in inactive button'
        },
        {
          name: 'maxHeight',
          type: 'Number | String',
          default: '360',
          description: 'Maximum height of the menu dropdown'
        },
        {
          name: 'minWidth',
          type: 'Number | String',
          default: '320',
          description: 'Minimum width of the menu dropdown'
        },
        {
          name: 'maxWidth',
          type: 'Number | String',
          default: '320',
          description: 'Maximum width of the menu dropdown'
        },
        {
          name: 'headerText',
          type: 'String',
          default: "''",
          description: 'Custom header text (defaults to "Filter by {label}")'
        },
        {
          name: 'showHeader',
          type: 'Boolean',
          default: 'true',
          description: 'Whether to show the header section'
        },
        {
          name: 'showFooter',
          type: 'Boolean',
          default: 'true',
          description: 'Whether to show the footer with clear/apply buttons'
        },
        {
          name: 'disableClear',
          type: 'Boolean',
          default: 'false',
          description: 'Disable the clear button'
        },
        {
          name: 'disableApply',
          type: 'Boolean',
          default: 'false',
          description: 'Disable the apply button'
        },
        {
          name: 'clearText',
          type: 'String',
          default: "'clear'",
          description: 'Custom text for clear button'
        },
        {
          name: 'applyText',
          type: 'String',
          default: "'apply'",
          description: 'Custom text for apply button'
        },
        {
          name: 'contentClass',
          type: 'String',
          default: "'custom-menu'",
          description: 'Custom class for menu content'
        }
      ]
    },
    filterMenuSlots() {
      return [
        {
          name: 'default',
          description: 'Main content area for filter controls'
        },
        {
          name: 'activator',
          description: 'Custom activator button (provides on, attrs, isActive, clear scope)'
        },
        {
          name: 'header',
          description: 'Custom header content (overrides default header)'
        },
        {
          name: 'footer-left',
          description: 'Custom left side of footer (overrides clear button)'
        },
        {
          name: 'footer-right',
          description: 'Custom right side of footer (overrides apply button)'
        }
      ]
    },
    filterMenuEvents() {
      return [
        {
          name: '@clear',
          parameters: '-',
          description: 'Emitted when the clear (X) button is clicked'
        },
        {
          name: '@apply',
          parameters: '-',
          description: 'Emitted when the apply button is clicked (menu closes automatically)'
        },
        {
          name: '@input',
          parameters: 'value: Boolean',
          description: 'Emitted when menu open state changes (for v-model support)'
        },
        {
          name: '@update:menu',
          parameters: 'value: Boolean',
          description: 'Emitted when menu open state changes'
        }
      ]
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.scrollToSection()
    })
  },
  watch: {
    '$route.hash': {
      handler() {
        this.scrollToSection()
      },
      immediate: false
    }
  },
  methods: {
    navigateToSection(sectionId) {
      this.$router.push({
        name: this.$route.name,
        hash: `#${sectionId}`,
        query: this.$route.query
      })
    },
    scrollToSection() {
      const sectionIdWithHash = this.$route.hash
      const sectionId = sectionIdWithHash.replace('#', '')
      if (sectionId) {
        this.$nextTick(() => {
          const element = document.getElementById(sectionId)
          if (element) {
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            })
          }
        })
      }
    },
    async copyCode(code) {
      try {
        await navigator.clipboard.writeText(code)
        this.copiedCode = code
        setTimeout(() => {
          this.copiedCode = null
        }, 2000)
      } catch (err) {
        console.error('Failed to copy code:', err)
      }
    }
  }
}
</script>

<style scoped>
.variations-grid {
  max-width: 100%;
}

.variation-item {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 20px;
  background-color: #ffffff;
}

.variation-preview {
  background-color: white;
  border: 1px dashed #d0d0d0 !important;
}

.code-block {
  background-color: #f8f9fa !important;
  border: 1px solid #e9ecef;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  line-height: 1.5;
}

.code-header {
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 8px;
}

.component-section {
  background-color: #ffffff;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  padding: 24px;
}

.props-table .v-data-table {
  border: 1px solid #e0e0e0;
}

.single-line {
  white-space: normal;
  word-wrap: break-word;
  max-width: 400px;
}

.text-no-wrap {
  white-space: nowrap;
}

.cursor-pointer {
  cursor: pointer;
  transition: color 0.2s ease;
}

.cursor-pointer:hover {
  color: #1976d2;
}

.hover-bg:hover {
  background-color: #f5f5f5;
}

.menu-header {
  position: sticky;
  top: 0;
  z-index: 1;
}
</style>
